"use client"
import { useEffect, useMemo, useRef, useState, useId } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'

type Product = any

type ActionType = 'rate' | 'support'
type Step = 'select' | 'form'

type VariantEntry = {
  id: string
  productId: number | string
  productSlug: string
  productTitle: string
  productType: string
  category: string
  color: string
  image: string
  amazonUrl: string | null
  asin: string | null
  sku?: string
}

const PRODUCT_TYPES = [
  'Rabbit fur runner rug',
  'Microfiber runner rug',
  'Door mat',
  'Combo',
] as const

const PRODUCT_TYPE_ORDER = new Map(PRODUCT_TYPES.map((t, i) => [t, i]))
const SUPPORT_FORM_INITIAL = { name: '', email: '', phone: '', orderId: '', message: '', botField: '' }

function productTypeFor(p: Product): string {
  const hay = `${p?.title || ''} ${p?.category || ''} ${p?.collection || ''} ${p?.slug || ''}`.toLowerCase()
  if (/rabbit|faux\s*fur|fur/.test(hay)) return 'Rabbit fur runner rug'
  if (/combo/.test(hay)) return 'Combo'
  if (/door\s*mat|floor\s*mat/.test(hay)) return 'Door mat'
  return 'Microfiber runner rug'
}

function extractAsin(url?: string | null, fallback?: string | null): string | null {
  const clean = String(url || '').trim()
  if (clean) {
    const dp = /\/(?:dp|gp\/product)\/([A-Z0-9]{10})/i.exec(clean)
    if (dp?.[1]) return dp[1].toUpperCase()
    const loose = /\/([A-Z0-9]{10})(?:[/?]|$)/i.exec(clean)
    if (loose?.[1]) return loose[1].toUpperCase()
  }
  const fb = String(fallback || '').trim()
  if (/^[A-Z0-9]{10}$/i.test(fb)) return fb.toUpperCase()
  return null
}

function validateEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim())
}

function validatePhone(value: string) {
  const raw = String(value || '').trim()
  if (!raw) return true
  const compact = raw.replace(/[\s-]/g, '')
  const e164 = /^\+?[1-9]\d{7,14}$/
  const digits = raw.replace(/[^\d]/g, '')
  return e164.test(compact) || digits.length === 10
}

type Toast = {
  type: 'success' | 'error'
  message: string
  actionLabel?: string
  actionHref?: string
  persistent?: boolean
}

export default function CareClient({ products }: { products: Product[] }) {
  const [modalOpen, setModalOpen] = useState(false)
  const [action, setAction] = useState<ActionType | null>(null)
  const [step, setStep] = useState<Step>('select')
  const [productType, setProductType] = useState<string>('All')
  const [color, setColor] = useState<string>('All colors')
  const [query, setQuery] = useState<string>('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [mounted, setMounted] = useState(false)
  const [toast, setToast] = useState<Toast | null>(null)
  const toastTimerRef = useRef<number | null>(null)
  const supportFormId = useId()
  const [supportForm, setSupportForm] = useState(SUPPORT_FORM_INITIAL)
  const [supportTouched, setSupportTouched] = useState({ name: false, email: false, phone: false, message: false })
  const [supportError, setSupportError] = useState('')
  const [supportStatus, setSupportStatus] = useState('')
  const [supportSending, setSupportSending] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const variants: VariantEntry[] = useMemo(() => {
    const out: VariantEntry[] = []
    for (const p of products || []) {
      const options = Array.isArray(p?.variants?.options) ? p.variants.options : []
      const type = productTypeFor(p)
      const baseAmazonUrl = (p as any)?.amazon?.url || (p as any)?.amazonUrl || null
      const baseAsin = (p as any)?.amazon?.asin || null
      if (!options.length) {
        const asin = extractAsin(baseAmazonUrl, baseAsin)
        out.push({
          id: `${p.id || p.slug}-base`,
          productId: p.id || p.slug,
          productSlug: p.slug,
          productTitle: p.title,
          productType: type,
          category: p.category || '',
          color: 'Default',
          image: p.cover || '',
          amazonUrl: baseAmazonUrl,
          asin,
          sku: p.sku,
        })
        continue
      }
      for (const opt of options) {
        const amazonUrl = opt?.amazonUrl || baseAmazonUrl || null
        const asin = extractAsin(amazonUrl, baseAsin)
        out.push({
          id: `${p.id || p.slug}-${opt.id || opt.sku || opt.label}`,
          productId: p.id || p.slug,
          productSlug: p.slug,
          productTitle: p.title,
          productType: type,
          category: p.category || '',
          color: opt?.label || 'Default',
          image: opt?.image || p.cover || '',
          amazonUrl,
          asin,
          sku: opt?.sku || p.sku,
        })
      }
    }
    return out
  }, [products])

  const colorOptions = useMemo(() => {
    const list = variants.filter(v => productType === 'All' || v.productType === productType)
    const set = new Set(list.map(v => v.color).filter(Boolean))
    return ['All colors', ...Array.from(set)]
  }, [variants, productType])

  useEffect(() => {
    if (productType === 'All') {
      setColor('All colors')
      return
    }
    if (!colorOptions.includes(color)) {
      setColor('All colors')
    }
  }, [productType, colorOptions, color])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = variants.filter(v => {
      if (productType !== 'All' && v.productType !== productType) return false
      if (color !== 'All colors' && v.color !== color) return false
      if (!q) return true
      return [
        v.productTitle,
        v.productType,
        v.color,
        v.category,
        v.sku || '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    })
    return list.sort((a, b) => {
      const ta = PRODUCT_TYPE_ORDER.get(a.productType) ?? 999
      const tb = PRODUCT_TYPE_ORDER.get(b.productType) ?? 999
      if (ta !== tb) return ta - tb
      const t = a.productTitle.localeCompare(b.productTitle)
      if (t !== 0) return t
      return a.color.localeCompare(b.color)
    })
  }, [variants, productType, color, query])

  const selected = useMemo(() => filtered.find(v => v.id === selectedId) || variants.find(v => v.id === selectedId) || null, [filtered, variants, selectedId])
  const reviewUrl = selected?.asin ? `https://www.amazon.in/review/create-review/?asin=${encodeURIComponent(selected.asin)}` : ''

  useEffect(() => {
    if (!modalOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [modalOpen])

  function openAction(next: ActionType) {
    setAction(next)
    setModalOpen(true)
    setStep('select')
    setProductType('All')
    setColor('All colors')
    setQuery('')
    setSelectedId(null)
    setSupportForm(SUPPORT_FORM_INITIAL)
    setSupportTouched({ name: false, email: false, phone: false, message: false })
    setSupportError('')
    setSupportStatus('')
    setSupportSending(false)
  }

  function closeModal() {
    setModalOpen(false)
    setAction(null)
    setStep('select')
  }

  function showToast(next: Toast) {
    setToast(next)
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
      toastTimerRef.current = null
    }
    if (!next.persistent) {
      toastTimerRef.current = window.setTimeout(() => {
        setToast(null)
        toastTimerRef.current = null
      }, 3500)
    }
  }

  function handleAmazonRate() {
    if (!reviewUrl) return
    try {
      window.open(reviewUrl, '_blank', 'noopener,noreferrer')
      closeModal()
      showToast({ type: 'success', message: 'Thanks for rating us on Amazon!' })
    } catch {
      showToast({
        type: 'error',
        message: 'Could not open Amazon. Please use View past orders to review your purchase.',
        actionLabel: 'View past orders',
        actionHref: 'https://www.amazon.in/gp/css/order-history',
        persistent: true,
      })
    }
  }

  async function onSupportSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (supportSending) return
    setSupportError('')
    setSupportStatus('Sending...')
    try {
      const valid = supportForm.name.trim() && validateEmail(supportForm.email) && validatePhone(supportForm.phone) && supportForm.message.trim()
      if (!valid) {
        setSupportTouched({ name: true, email: true, phone: true, message: true })
        setSupportError('Please fill all required fields with valid details before sending.')
        setSupportStatus('')
        return
      }
      setSupportSending(true)
      const productLine = selected
        ? [
            `Product: ${selected.productTitle}`,
            `Type: ${selected.productType}`,
            `Color: ${selected.color}`,
            selected.sku ? `SKU: ${selected.sku}` : '',
          ].filter(Boolean).join('\n')
        : ''
      const message = `${productLine}\n\n${supportForm.message}`.trim()
      const subject = selected ? `Care support - ${selected.productTitle}` : 'Care support'
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: supportForm.name,
          email: supportForm.email,
          phone: supportForm.phone,
          orderId: supportForm.orderId,
          subject,
          message,
          botField: supportForm.botField || '',
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || 'send_failed')
      setSupportSending(false)
      setSupportStatus('')
      closeModal()
      showToast({ type: 'success', message: 'Thanks! Your message has been sent.' })
    } catch (err) {
      setSupportSending(false)
      setSupportStatus('')
      setSupportError('Could not send message. Please try again or use the contact page.')
      showToast({
        type: 'error',
        message: 'Could not send email. Please contact us via the Contact page.',
        actionLabel: 'Contact support',
        actionHref: '/contact',
        persistent: true,
      })
    }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <header className="space-y-3 text-center">
        <div className="inline-flex items-center justify-center rounded-full border bg-white px-3 py-1 text-xs uppercase tracking-widest text-gray-600">
          Order care
        </div>
        <h1 className="text-2xl md:text-3xl font-semibold">Thanks for placing your order</h1>
        <p className="text-sm text-gray-600">
          We hope you love your Saffron Leo piece. Use the options below to rate your purchase, review it, or reach support.
        </p>
      </header>

      <section className="grid gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => openAction('rate')}
          className="border bg-white px-5 py-5 shadow-sm text-left hover:shadow-md transition"
        >
          <div className="text-sm uppercase tracking-wide text-gray-500">Rate us on Amazon</div>
          <div className="mt-1 text-lg font-semibold">Share a quick rating</div>
          <div className="mt-2 text-sm text-gray-600">Select your product and jump to the Amazon review form.</div>
        </button>

        <button
          type="button"
          onClick={() => openAction('support')}
          className="border bg-white px-5 py-5 shadow-sm text-left hover:shadow-md transition"
        >
          <div className="text-sm uppercase tracking-wide text-gray-500">Contact support</div>
          <div className="mt-1 text-lg font-semibold">We are here to help</div>
          <div className="mt-2 text-sm text-gray-600">Select your product and send us your query.</div>
        </button>

        <a
          href="https://www.amazon.in/ryp"
          target="_blank"
          rel="noreferrer"
          className="border bg-white px-5 py-5 shadow-sm text-left hover:shadow-md transition"
        >
          <div className="text-sm uppercase tracking-wide text-gray-500">Review your purchase</div>
          <div className="mt-1 text-lg font-semibold">Write a detailed review</div>
          <div className="mt-2 text-sm text-gray-600">Go to your Amazon review hub.</div>
        </a>

        <a
          href="https://www.amazon.in/gp/css/order-history"
          target="_blank"
          rel="noreferrer"
          className="border bg-white px-5 py-5 shadow-sm text-left hover:shadow-md transition"
        >
          <div className="text-sm uppercase tracking-wide text-gray-500">Order history</div>
          <div className="mt-1 text-lg font-semibold">View past orders</div>
          <div className="mt-2 text-sm text-gray-600">Track updates and delivery details.</div>
        </a>
      </section>

      <Link href="/blog?category=Care%20%26%20Maintenance" className="block">
        <section className="border bg-white px-6 py-6 shadow-sm space-y-2 text-sm text-gray-600 hover:shadow-md transition">
          <div className="font-semibold text-gray-900">Need care tips?</div>
          <p>Browse care and maintenance guides for your Saffron Leo rug.</p>
        </section>
      </Link>

      {toast ? (
        <div className="fixed inset-x-0 bottom-4 z-[95] flex justify-center px-4">
          <div className={`w-full max-w-xl rounded border px-4 py-3 text-sm shadow-lg flex items-start justify-between gap-4 ${toast.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-900'}`}>
            <div className="min-w-0">
              <div className="font-medium">{toast.message}</div>
              {toast.actionHref && toast.actionLabel ? (
                <a
                  href={toast.actionHref}
                  target={toast.actionHref.startsWith('http') ? '_blank' : undefined}
                  rel={toast.actionHref.startsWith('http') ? 'noreferrer' : undefined}
                  className="mt-1 inline-flex text-xs underline decoration-dotted"
                >
                  {toast.actionLabel}
                </a>
              ) : null}
            </div>
            <button type="button" onClick={() => setToast(null)} className="shrink-0 rounded border px-2 py-1 text-xs hover:bg-white/60">Close</button>
          </div>
        </div>
      ) : null}

      {modalOpen && action && mounted ? createPortal(
        <div className="fixed inset-0 z-[90]">
          <button
            type="button"
            aria-label="Close"
            className="fixed inset-0 bg-black/40"
            onClick={closeModal}
          />
          <div className="absolute inset-0 w-full h-[100dvh] max-h-[100dvh] rounded-none border-0 bg-white shadow-none overflow-hidden flex flex-col sm:relative sm:mx-auto sm:mt-8 sm:w-[92vw] sm:max-w-5xl sm:max-h-[90vh] sm:rounded sm:border sm:shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4 flex-shrink-0">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  {action === 'rate' ? 'Rate us on Amazon' : 'Contact support'}
                </div>
                <div className="text-lg font-semibold">
                  {step === 'select' ? 'Select your product' : 'Share your details'}
                </div>
              </div>
              <button type="button" onClick={closeModal} className="inline-flex h-9 w-9 items-center justify-center rounded border hover:bg-gray-50">
                ✕
              </button>
            </div>

            {step === 'select' ? (
              <>
                <div className="min-h-0 flex-1 overflow-y-auto p-5 space-y-4">
                  <div className="grid gap-3 md:grid-cols-[1fr_1fr_2fr]">
                    <label className="space-y-1 text-sm">
                      <span className="text-gray-600">Product type</span>
                      <select
                        value={productType}
                        onChange={(e) => setProductType(e.target.value)}
                        className="w-full rounded border px-3 py-2 bg-white text-sm"
                      >
                        <option value="All">All product types</option>
                        {PRODUCT_TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="text-gray-600">Color</span>
                      <select
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        className="w-full rounded border px-3 py-2 bg-white text-sm"
                        disabled={productType === 'All'}
                      >
                        {colorOptions.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="text-gray-600">Search</span>
                      <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search product name, color, or SKU"
                        className="w-full rounded border px-3 py-2 text-sm"
                      />
                    </label>
                  </div>

                  <div className="overflow-y-auto border rounded sm:max-h-[50vh]">
                    {filtered.length ? (
                      <div className="grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3">
                        {filtered.map((v) => {
                          const isSelected = v.id === selectedId
                          return (
                            <button
                              key={v.id}
                              type="button"
                              onClick={() => setSelectedId(v.id)}
                              className={`flex gap-3 rounded border px-3 py-3 text-left transition ${
                                isSelected ? 'border-brand ring-2 ring-brand/30' : 'border-gray-200 hover:border-gray-400'
                              }`}
                            >
                              <div className="h-16 w-16 overflow-hidden rounded border bg-gray-50 flex-shrink-0">
                                {v.image ? (
                                  <img src={v.image} alt={`${v.productTitle} - ${v.color}`} className="h-full w-full object-cover" loading="lazy" decoding="async" />
                                ) : null}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-semibold line-clamp-2">{v.productTitle}</div>
                                <div className="mt-1 text-xs text-gray-500">{v.productType}</div>
                                <div className="text-xs text-gray-700">Color: {v.color}</div>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    ) : (
                      <div className="p-5 text-sm text-gray-600">No products match your filters.</div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 border-t px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-white">
                  <div className="flex items-center justify-between gap-3 flex-nowrap">
                    <div className="text-xs text-gray-500 min-w-0 break-words">
                      {selected ? `Selected: ${selected.productTitle} - ${selected.color}` : 'Select your ordered product to continue.'}
                    </div>
                    {action === 'rate' ? (
                      <button
                        type="button"
                        onClick={handleAmazonRate}
                        disabled={!reviewUrl}
                        className={`inline-flex items-center justify-center rounded border px-4 py-2 text-sm font-medium shrink-0 whitespace-nowrap ${
                          reviewUrl ? 'border-brand bg-white text-black hover:bg-brand hover:text-white' : 'text-gray-400'
                        }`}
                      >
                        Rate us on Amazon
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setStep('form')}
                        disabled={!selected}
                        className={`inline-flex items-center justify-center rounded border px-4 py-2 text-sm font-medium shrink-0 whitespace-nowrap ${
                          selected ? 'border-brand bg-white text-black hover:bg-brand hover:text-white' : 'text-gray-400'
                        }`}
                      >
                        Next
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <SupportFormFields
                    formId={supportFormId}
                    form={supportForm}
                    setForm={setSupportForm}
                    touched={supportTouched}
                    setTouched={setSupportTouched}
                    error={supportError}
                    status={supportStatus}
                    selected={selected}
                    onSubmit={onSupportSubmit}
                  />
                </div>
                <div className="flex-shrink-0 border-t px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-white">
                  <div className="flex items-center justify-between gap-3 flex-nowrap">
                    <button type="button" onClick={() => setStep('select')} className="text-sm underline decoration-dotted">
                      Change product
                    </button>
                    <button
                      type="submit"
                      form={supportFormId}
                      disabled={supportSending}
                      className={`inline-flex items-center justify-center rounded border px-5 py-2 text-sm font-medium shrink-0 whitespace-nowrap ${
                        supportSending ? 'text-gray-400' : 'border-brand bg-white text-black hover:bg-brand hover:text-white'
                      }`}
                    >
                      {supportSending ? 'Sending...' : 'Send message'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  )
}

function SupportFormFields({
  formId,
  form,
  setForm,
  touched,
  setTouched,
  error,
  status,
  selected,
  onSubmit,
}: {
  formId: string
  form: typeof SUPPORT_FORM_INITIAL
  setForm: (value: typeof SUPPORT_FORM_INITIAL | ((prev: typeof SUPPORT_FORM_INITIAL) => typeof SUPPORT_FORM_INITIAL)) => void
  touched: { name: boolean; email: boolean; phone: boolean; message: boolean }
  setTouched: (value: { name: boolean; email: boolean; phone: boolean; message: boolean } | ((prev: { name: boolean; email: boolean; phone: boolean; message: boolean }) => { name: boolean; email: boolean; phone: boolean; message: boolean })) => void
  error: string
  status: string
  selected: VariantEntry | null
  onSubmit: (e: React.FormEvent) => void
}) {
  return (
    <div className="p-5 space-y-4">
      <div className="rounded border bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <div className="font-semibold text-gray-900">Selected product</div>
        <div className="mt-1">{selected ? `${selected.productTitle} - ${selected.color}` : 'No product selected'}</div>
      </div>

      <form id={formId} onSubmit={onSubmit} className="space-y-4">
        <input type="text" name="bot-field" value={form.botField} onChange={e => setForm({ ...form, botField: (e.target as HTMLInputElement).value })} className="hidden" aria-hidden />

        {error ? (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-gray-700">Full name <span className="text-red-600" aria-hidden>*</span></span>
            <input required value={form.name} onChange={e => setForm({ ...form, name: (e.target as HTMLInputElement).value })} onBlur={() => setTouched(t => ({ ...t, name: true }))} aria-invalid={touched.name && !form.name.trim()} className="w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="Your name" />
            {touched.name && !form.name.trim() ? (<p className="text-xs text-red-600 mt-1">Please enter your name.</p>) : null}
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-700">Email <span className="text-red-600" aria-hidden>*</span></span>
            <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: (e.target as HTMLInputElement).value })} onBlur={() => setTouched(t => ({ ...t, email: true }))} aria-invalid={touched.email && !validateEmail(form.email)} className="w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="you@example.com" />
            {touched.email && !validateEmail(form.email) ? (<p className="text-xs text-red-600 mt-1">Please enter a valid email address.</p>) : null}
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-gray-700">Contact number <span className="text-gray-400" aria-hidden>(optional)</span></span>
            <input type="tel" inputMode="tel" value={form.phone} onChange={e => setForm({ ...form, phone: (e.target as HTMLInputElement).value })} onBlur={() => setTouched(t => ({ ...t, phone: true }))} aria-invalid={touched.phone && !validatePhone(form.phone)} className="w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="+91 98765 43210" />
            {touched.phone && form.phone && !validatePhone(form.phone) ? (<p className="text-xs text-red-600 mt-1">Please enter a valid phone number.</p>) : null}
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-700">Order ID (optional)</span>
            <input value={form.orderId} onChange={e => setForm({ ...form, orderId: (e.target as HTMLInputElement).value })} className="w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="e.g. 404-1234567-8901234" />
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-gray-700">Ordered product</span>
            <input value={selected?.productTitle || ''} readOnly className="w-full rounded border px-3 py-2 bg-gray-50 text-sm" />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-700">Order color</span>
            <input value={selected?.color || ''} readOnly className="w-full rounded border px-3 py-2 bg-gray-50 text-sm" />
          </label>
        </div>

        <label className="space-y-1 text-sm">
          <span className="text-gray-700">Message <span className="text-red-600" aria-hidden>*</span></span>
          <textarea required rows={5} value={form.message} onChange={e => setForm({ ...form, message: (e.target as HTMLTextAreaElement).value })} onBlur={() => setTouched(t => ({ ...t, message: true }))} aria-invalid={touched.message && !form.message.trim()} className="w-full resize-y rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/70" placeholder="Tell us how we can help..." />
          {touched.message && !form.message.trim() ? (<p className="text-xs text-red-600 mt-1">Please write a short message.</p>) : null}
        </label>

        {status ? <p className="text-xs text-gray-600">{status}</p> : null}
      </form>
    </div>
  )
}
